import { CircularProgress, Container, Stack, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useObservable } from "@ngneat/react-rxjs";
import { useSnackbar } from "notistack";

import { entityDetailsQuery, entityDetailsService } from "@store/entities/details";
import { EntityTypeEnum } from "@store/entities/entities.model";

import ActionsTimelineComponent from "@components/ActionsTimeline.component";
import BackToTopButton from "@components/BackToTopButton.component";

interface EntityHistoricTabProps {
  type: EntityTypeEnum;
}

const EntityHistoricTab = (props: EntityHistoricTabProps) => {
  const {type} = props;

  const { enqueueSnackbar } = useSnackbar();
  const { entityId } = useParams();

  const theme = useTheme();
  const breakpointDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const [{ entityActions: actions, loading }] = useObservable(entityDetailsQuery.actions$(type));
  const [paginatedMetaData] = useObservable(entityDetailsQuery.actionsPaginatedMeta$(type));

  useEffect(() => {
    if (entityId) {
      entityDetailsService.getEntityHistory(type, entityId, currentPage, itemsPerPage).subscribe({
        error: (err: any) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [entityId, enqueueSnackbar, type, currentPage, itemsPerPage]);

  const backToTop = () => {
    if (scrollableRef.current)
      scrollableRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
  };

  return (
    <Stack width="100%" alignItems="center" py={breakpointDownSM ? "20px" : "40px"} overflow="auto" className="scrollable" ref={scrollableRef}>
      <Container maxWidth="md" sx={{ px: breakpointDownSM ? "4px" : undefined }}>
        <Stack width="100%">
          {loading ? (
            <CircularProgress sx={{ alignSelf: "center" }} size={20} />
          ) : (
            <ActionsTimelineComponent
              actions={actions ?? []}
              setCurrentPage={setCurrentPage}
              paginatedMetaData={paginatedMetaData}
              breakpointDownSM={breakpointDownSM}
            />
          )}
        </Stack>
      </Container>
      <Stack direction="row" width="100%" justifyContent="center" mt="20px" height={"80px"} alignItems="center">
        {loading && <CircularProgress size={30} />}
      </Stack>
      <BackToTopButton onClick={backToTop} posRight={breakpointDownSM ? "25px" : "60px"} />
    </Stack>
  );
};

export default EntityHistoricTab;
